import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect, useRef } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Link as RouterLink } from '@reach/router';
import { useLazyQuery, useQuery } from '@apollo/client';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
// material
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@mui/material/styles/styled';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import { useAllowByPermission } from '../../../../../hooks';
import { useIsPolicyStreamOrganization } from '../../../../PolicyStream/hooks/useIsPolicyStreamOrganization';
import { PolicystreamDownloadClientCsvButton } from './PolicystreamDownloadClientCsvButton';
import { useAuth } from '@calefy-inc/authentication';
// components
import Label from '../../../components/Label';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  QuoteListHead,
  QuoteListToolbar,
} from '../../../components/_dashboard/quotes/list';
import LoadingScreen from '../../../components/LoadingScreen';
import { InsufficientPermissionsErrorPage } from '../../../../common/ErrorHandling';
// utils
import { findLegalOrTradeNameFromStructuredData } from '../../../utils/AnswerSearch';
import { downloadPdfFile } from '../../../../common/QuoteLoader/utility';
import { generateColourFromQuoteStatus, escapeCsvValue } from '../utility';
import { format } from 'date-fns';
import { toTitleCase } from '../../../../../util/toTitleCase';
import { getUtcOffset } from '../../../../../util';
// queries
import {
  RELAY_QUOTES,
  GET_QUOTE_PDF,
  GET_ALL_QUOTES,
  GET_ALL_REFERRAL_CODES,
} from '../../../../../queries';
// error handler
import Bugsnag from '@bugsnag/js';
import { errorify } from '../../../../../util/errorify';
// pages
import ErrorPage from '../../Page500';
import { useSnackbar } from 'notistack';

// classes and types
import type { $TSFixMe, ArrayElement } from '@calefy-inc/utilityTypes';
import type {
  AxisStructuredDataType,
  RelayQuotesQuery,
} from '../../../../../gql/graphql';
import { downloadFile } from '@calefy-inc/utility';

type QuoteEdge = NonNullable<
  ArrayElement<NonNullable<RelayQuotesQuery['relayQuotes']>['edges']>
>;

// ----------------------------------------------------------------------

export function BasicQuoteList() {
  const theme = useTheme();
  const { token } = useAuth();
  const isPolicyStreamOrganization = useIsPolicyStreamOrganization();
  const hasCorrectPermissions = useAllowByPermission('view:quotes:table');
  const { enqueueSnackbar } = useSnackbar();
  const [quoteList, setQuoteList] = useState<Array<$TSFixMe>>([]);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<$TSFixMe>('dateAdded');
  const [filterName, setFilterName] = useState<string>(''); // the string to search for
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [endCursor, setEndcursor] = useState<$TSFixMe>();
  const [hasNextPage, setHasNextPage] = useState<$TSFixMe>();
  const [allFetchedQuotes, setAllFetchedQuotes] = useState<Array<$TSFixMe>>([]);
  const [timeOut, setTimeOut] = useState<$TSFixMe>();
  const [hiddenQueryLoading, setHiddenQueryLoading] = useState<$TSFixMe>();
  const [generatingPdf, setGeneratingPdf] = useState<$TSFixMe>();
  const [showActionId, setShowActionId] = useState<$TSFixMe>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDateFilterLoading, setIsDateFilterLoading] = useState(false);
  const replaceQuotesOnNextFetch = useRef(false);
  const isInitialDateFilterMount = useRef(true);
  const [tableHead, setTableHead] = useState<
    Array<{ id: string; label: string; alignRight?: boolean }>
  >([]);
  const { data: allReferralCodesData } = useQuery(GET_ALL_REFERRAL_CODES, {
    variables: { token },
    skip: !token,
  });

  // Relay quotes query
  const [getQuotes, { loading, data, error }] = useLazyQuery(RELAY_QUOTES, {
    fetchPolicy: 'network-only',
  });

  // CSV export query - fetches all matching quotes without pagination limit
  const [getCsvQuotes, { loading: csvQueryLoading }] = useLazyQuery(
    RELAY_QUOTES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (csvQueryData: RelayQuotesQuery) => {
        try {
          const quotes = (csvQueryData.relayQuotes?.edges ?? [])
            .filter((edge): edge is QuoteEdge => edge !== null)
            .map((edge: QuoteEdge) => getQuoteWithCompanyName(edge.node));
          const headers = [
            'Date',
            'Client',
            'Line',
            'Unique ID',
            'Status',
            'Link',
          ];
          const hasCodes =
            allReferralCodesData?.getAllReferralCodes &&
            allReferralCodesData.getAllReferralCodes.length > 0;
          if (hasCodes) headers.push('Referral Code');

          const rows = quotes.map((row: $TSFixMe) => {
            const rowData = {
              date: format(new Date(row.dateAdded), 'MMMM dd yyyy, p'),
              client: row.company || row.contactName || '',
              business: row.business || '',
              uniqueID: row.friendlyId || row.uniqueID || '',
              status: row.status || '',
              referralCode: row.referralCode?.code || '',
              link: row.linkToDetailsPage || '',
            };
            const cells = [
              rowData.date,
              rowData.client,
              rowData.business,
              rowData.uniqueID,
              rowData.status,
            ];
            if (hasCodes) cells.push(rowData.referralCode);
            cells.push(rowData.link);
            return cells.map(escapeCsvValue).join(',');
          });

          const csvContent = btoa([headers.join(','), ...rows].join('\n'));
          downloadFile(
            csvContent,
            `Application List ${new Date().toDateString()}.csv`,
            'data:text/csv;base64',
          );
        } catch (error) {
          enqueueSnackbar('CSV Download Failed', { variant: 'error' });
          Bugsnag.notify(errorify(error));
        }
      },
      onError: (error) => {
        enqueueSnackbar('CSV Download Failed', { variant: 'error' });
        Bugsnag.notify(errorify(error));
      },
    },
  );

  // Search db query
  const [
    getSearchedQuotes,
    { data: searchedData, loading: searchedLoading, error: searchedError },
  ] = useLazyQuery(GET_ALL_QUOTES);

  // Load pdf query
  const [
    loadQuotePdf,
    { data: pdfData, loading: pdfLoading, error: pdfError },
  ] = useLazyQuery(GET_QUOTE_PDF);

  useEffect(() => {
    if (token && hasCorrectPermissions) {
      getQuotes({
        variables: {
          token: token,
          startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
          endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
          utcOffset: getUtcOffset(),
        },
      });
    }
  }, [token, hasCorrectPermissions, getQuotes]);

  // Refetch when date range changes, keeping existing rows until new data arrives
  useEffect(() => {
    if (isInitialDateFilterMount.current) {
      isInitialDateFilterMount.current = false;
      return;
    }
    if (token && hasCorrectPermissions) {
      replaceQuotesOnNextFetch.current = true;
      setIsDateFilterLoading(true);
      setEndcursor(undefined);
      setHasNextPage(undefined);
      setPage(0);
      getQuotes({
        variables: {
          token,
          startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
          endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
          utcOffset: getUtcOffset(),
        },
      });
    }
  }, [startDate, endDate, token, hasCorrectPermissions]);

  // Set table head based on whether organization has referral codes
  useEffect(() => {
    const baseTableHead = [
      { id: 'dateAdded', label: 'Date', alignRight: false },
      { id: 'company', label: 'Client', alignRight: false },
      { id: 'business', label: 'Line', alignRight: false },
      { id: 'uniqueID', label: 'Unique ID', alignRight: false },
      { id: 'status', label: 'Status', alignRight: false },
    ];

    // Only include referral code column if organization has referral codes
    const hasReferralCodes =
      allReferralCodesData?.getAllReferralCodes &&
      allReferralCodesData.getAllReferralCodes.length > 0;

    if (hasReferralCodes) {
      baseTableHead.push({
        id: 'referralCode',
        label: 'Referral Code',
        alignRight: false,
      });
    }

    // Add the action column at the end
    baseTableHead.push({ id: '', label: '', alignRight: false });

    setTableHead(baseTableHead);
  }, [allReferralCodesData]);

  useEffect(() => {
    clearTimeout(timeOut);
    if (filterName) {
      setTimeOut(
        setTimeout(() => {
          searchQuery(filterName);
        }, 500),
      );
    }

    if (!filterName) {
      setQuoteList(allFetchedQuotes);
      setPage(0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterName]);

  useEffect(() => {
    if (pdfData && pdfData.quotePdf?.pdfString) {
      const quotePdf = pdfData.quotePdf;
      downloadPdfFile(
        // @ts-expect-error
        quotePdf.pdfString,
        `${quotePdf.businessName || 'New Application'}.pdf`,
      );
      enqueueSnackbar('Download Complete', { variant: 'success' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData]);

  useEffect(() => {
    if (pdfError) {
      enqueueSnackbar('Download Failed', { variant: 'error' });
      Bugsnag.notify(
        `Pdf download failed: ${JSON.stringify(pdfError, null, 4)}`,
      );
    } else if (pdfData && !pdfData.quotePdf?.pdfString) {
      enqueueSnackbar('Download Failed - Unable to Generate PDF', {
        variant: 'error',
      });
      Bugsnag.notify(
        `Pdf download failed: ${JSON.stringify(pdfData, null, 4)}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfError, pdfData]);

  useEffect(() => {
    if (searchedData) {
      // @ts-expect-error
      const quotesWithCompanyName = searchedData.allQuotes.map(
        (quote: $TSFixMe) => {
          return getQuoteWithCompanyName(quote);
        },
      );

      setQuoteList(quotesWithCompanyName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedData]);

  useEffect(() => {
    if (
      hasNextPage &&
      rowsPerPage * (page + 2) >= quoteList.length &&
      !searchedData
    ) {
      getQuotes({
        variables: {
          first: 100,
          cursor: endCursor,
          token,
          startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
          endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
          utcOffset: getUtcOffset(),
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteList, rowsPerPage, page]);

  useEffect(() => {
    if (data) {
      // @ts-expect-error
      const quotesWithCompanyName = data.relayQuotes.edges.map(
        (quote: $TSFixMe) => {
          return getQuoteWithCompanyName(quote.node);
        },
      );

      if (replaceQuotesOnNextFetch.current) {
        replaceQuotesOnNextFetch.current = false;
        setQuoteList(quotesWithCompanyName);
        setAllFetchedQuotes(quotesWithCompanyName);
        setIsDateFilterLoading(false);
      } else {
        setQuoteList([...quoteList, ...quotesWithCompanyName]);
        setAllFetchedQuotes([...quoteList, ...quotesWithCompanyName]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  //console.log({ allFetchedQuotes });
  useEffect(() => {
    if (data) {
      // @ts-expect-error
      setEndcursor(data.relayQuotes.pageInfo.endCursor);
      // @ts-expect-error
      setHasNextPage(data.relayQuotes.pageInfo.hasNextPage);
    }
  }, [data]);

  useEffect(() => {
    const anyLoading = loading || searchedLoading;
    if (
      anyLoading &&
      quoteList.length > 0 &&
      !replaceQuotesOnNextFetch.current
    ) {
      setHiddenQueryLoading(true);
    } else {
      setHiddenQueryLoading(false);
    }

    if (pdfLoading) {
      setGeneratingPdf(true);
    } else {
      setGeneratingPdf(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchedLoading, pdfLoading]);

  useEffect(() => {
    if (error && replaceQuotesOnNextFetch.current) {
      replaceQuotesOnNextFetch.current = false;
      setIsDateFilterLoading(false);
    }
  }, [error]);

  // Check current status of queries
  // Skip full-screen loader during date-filter refetches (list may already be empty)
  if (
    (loading && quoteList.length === 0 && !replaceQuotesOnNextFetch.current) ||
    !token
  ) {
    return <LoadingScreen />;
  }

  if (error || pdfError || searchedError) {
    const anyError = error || pdfError || searchedError;
    console.error('Error:', anyError);
    // @ts-expect-error
    Bugsnag.notify(anyError);
    return <ErrorPage />;
  }

  const handleChangePage = (_event: $TSFixMe, newPage: $TSFixMe) => {
    setPage(newPage);
  };

  const handleRequestSort = (_event: $TSFixMe, property: $TSFixMe) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (event: $TSFixMe) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event: $TSFixMe) => {
    setFilterName(event.target.value);
  };

  const searchQuery = (searchState: $TSFixMe) => {
    if (searchState) {
      getSearchedQuotes({
        variables: { token, search: searchState },
      });

      setPage(0);
    }
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - quoteList.length) : 0;

  const filteredQuotes = applySortFilter(
    quoteList,
    getComparator(order, orderBy),
  );

  const isQuoteNotFound = filteredQuotes.length === 0;

  const hasReferralCodes =
    allReferralCodesData?.getAllReferralCodes &&
    allReferralCodesData.getAllReferralCodes.length > 0;

  const getRowData = (row: $TSFixMe) => ({
    date: format(new Date(row.dateAdded), 'MMMM dd yyyy, p'),
    client: row.company || row.contactName || '',
    business: row.business || '',
    uniqueID: row.friendlyId || row.uniqueID || '',
    status: row.status || '',
    referralCode: row.referralCode?.code || '',
  });

  const downloadCsv = () => {
    getCsvQuotes({
      variables: {
        token,
        first: 99999,
        startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
        endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
        utcOffset: getUtcOffset(),
      },
    });
  };

  return (
    <>
      {!!token && hasCorrectPermissions === false ? (
        <InsufficientPermissionsErrorPage deniedIdentifier='Application Table' />
      ) : (
        <>
          <HeaderBreadcrumbs
            heading='Application List'
            links={[
              { name: 'Dashboard', href: PATH_DASHBOARD.general.dashboard },
              { name: 'Applications' },
            ]}
            action={
              <ApplicationListButtons>
                {isPolicyStreamOrganization ? (
                  <PolicystreamDownloadClientCsvButton />
                ) : null}
                <Button
                  variant='outlined'
                  startIcon={
                    csvQueryLoading ? (
                      <CircularProgress size='1rem' />
                    ) : (
                      <DownloadRoundedIcon />
                    )
                  }
                  onClick={downloadCsv}
                  disabled={csvQueryLoading}
                >
                  Download CSV
                </Button>
                <Button
                  variant='contained'
                  component={RouterLink}
                  to={'/insurtech/manager/quotes/create'}
                  startIcon={<Icon icon={plusFill} />}
                >
                  New Application
                </Button>
              </ApplicationListButtons>
            }
          />
          <Card>
            <QuoteListToolbar
              filterName={filterName}
              onFilterName={handleFilterByName}
              isLoading={hiddenQueryLoading}
              generatingPdf={generatingPdf}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              isDateFilterLoading={isDateFilterLoading}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, minHeight: 400 }}>
                <Table>
                  <QuoteListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={tableHead}
                    rowCount={quoteList.length}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {filteredQuotes
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => {
                        const { id, uniqueID, referralCode } = row;
                        const rowData = getRowData(row);

                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            onMouseEnter={() => {
                              setShowActionId(row.id);
                            }}
                            onMouseLeave={() => setShowActionId(-1)}
                          >
                            {(() => {
                              const tableCells = [
                                rowData.date,
                                rowData.client || '...',
                                rowData.business,
                                rowData.uniqueID,
                                <Label
                                  variant={
                                    theme.palette.mode === 'light'
                                      ? 'ghost'
                                      : 'filled'
                                  }
                                  color={generateColourFromQuoteStatus(
                                    rowData.status,
                                  )}
                                >
                                  {sentenceCase(rowData.status)}
                                </Label>,
                              ];

                              if (hasReferralCodes) {
                                tableCells.push(
                                  referralCode?.code ? (
                                    <Tooltip
                                      title={
                                        referralCode.description ||
                                        referralCode.code
                                      }
                                    >
                                      <span>{referralCode.code}</span>
                                    </Tooltip>
                                  ) : (
                                    ''
                                  ),
                                );
                              }

                              return tableCells;
                            })().map((elem: $TSFixMe) =>
                              // @ts-expect-error
                              // prettier-ignore
                              <TableCell component={RouterLink}
                                align='left'
                                to={`/insurtech/manager/quotes/details/${uniqueID}`}
                                sx={{ textDecoration: 'none' }}
                              >
                                {elem}
                              </TableCell>,
                            )}

                            <TableCell align='right'>
                              {row.id === showActionId ? (
                                <IconButton
                                  sx={{ color: 'text.secondary' }}
                                  onClick={() => {
                                    if (token) {
                                      loadQuotePdf({
                                        variables: {
                                          token,
                                          uniqueID,
                                        },
                                      });
                                    }
                                  }}
                                >
                                  <DownloadRoundedIcon />
                                </IconButton>
                              ) : (
                                <IconButton sx={{ visibility: 'hidden' }}>
                                  <DownloadRoundedIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={tableHead.length} />
                      </TableRow>
                    )}
                  </TableBody>
                  {isQuoteNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          align='center'
                          colSpan={tableHead.length}
                          sx={{ py: 3 }}
                        >
                          <SearchNotFound searchQuery={filterName} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component='div'
              count={quoteList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Card>
        </>
      )}
    </>
  );
}

const ApplicationListButtons = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    gap: theme.spacing(1),
  };
});

function getQuoteWithCompanyName(quote: $TSFixMe) {
  let clientName = undefined;
  if (
    quote.axisStructuredData?.clientFirstName ||
    quote.axisStructuredData?.clientLastName
  ) {
    const firstName = quote.axisStructuredData.clientFirstName || '';
    const lastName = quote.axisStructuredData.clientLastName || '';
    clientName = `${firstName} ${lastName}`.trim();
  }
  const axisLineDisplay = formatAxisLineDisplay(quote.axisStructuredData);
  return {
    ...quote,
    business: axisLineDisplay || quote.businessLine?.displayName || '',
    company: clientName
      ? clientName
      : findLegalOrTradeNameFromStructuredData(
          quote.structuredData,
          quote.businessLine?.displayName ?? '',
        ) || '',
    contactName: quote?.structuredData?.contactName,
  };
}

function formatAxisLineDisplay(
  axisStructuredData: AxisStructuredDataType | null,
): string | undefined {
  if (!axisStructuredData) return undefined;

  const { type, industry, subIndustry } = axisStructuredData;
  const result = [type, industry, subIndustry]
    .filter(Boolean)
    .map((value) => toTitleCase(String(value).split('_').join(' ')))
    .join(' - ');

  return result || undefined;
}

function applySortFilter(array: Array<$TSFixMe>, comparator: $TSFixMe) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order: 'asc' | 'desc', orderBy: $TSFixMe) {
  if (orderBy === 'referralCode') {
    const comparator = (a: $TSFixMe, b: $TSFixMe) => {
      const aCode = a.referralCode?.code || '';
      const bCode = b.referralCode?.code || '';
      return aCode.localeCompare(bCode);
    };
    return order === 'desc'
      ? comparator
      : (a: $TSFixMe, b: $TSFixMe) => -comparator(a, b);
  }
  return order === 'desc'
    ? (a: $TSFixMe, b: $TSFixMe) => descendingComparator(a, b, orderBy)
    : (a: $TSFixMe, b: $TSFixMe) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a: $TSFixMe, b: $TSFixMe, orderBy: $TSFixMe) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}
